import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { challenges } from '../data/mock';
import { CheckCircle2, ArrowRight, Phone } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollAnimation';

const ChallengePage = () => {
  const location = useLocation();
  const challengeId = location.pathname.replace('/', '');
  const challenge = challenges[challengeId];

  const [mainRef, mainVisible] = useScrollReveal({ threshold: 0.1 });
  const [pointsRef, pointsVisible] = useScrollReveal({ threshold: 0.1 });

  if (!challenge) {
    return (
      <div>
        <PageHeader title="Page Not Found" breadcrumbs={[{ label: 'Your Challenges' }]} />
        <div className="py-20 text-center">
          <p className="text-muted-foreground mb-4">The page you're looking for doesn't exist.</p>
          <Link to="/" style={{ color: '#43a047' }} className="text-sm hover:underline">Go back home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-enter">
      <PageHeader
        title={challenge.title}
        breadcrumbs={[
          { label: 'Your Challenges', href: '/your-challenges' },
          { label: challenge.title },
        ]}
      />

      <div className="py-14 bg-background">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-10">

            {/* ── Main ── */}
            <div className="flex-1">
              <div ref={mainRef} className={`${mainVisible ? 'anim-slide-right' : 'reveal-hidden'}`}>
                <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#43a047' }}>Challenge</p>
                <h2 className="text-3xl font-extrabold text-foreground mb-2">{challenge.subtitle}</h2>
                <div className="w-14 h-1 rounded-full mb-5" style={{ background: '#43a047' }} />
                <p className="text-muted-foreground leading-relaxed mb-10">{challenge.description}</p>
              </div>

              <h3 className={`text-lg font-bold text-foreground mb-5 ${mainVisible ? 'anim-fade-up delay-200' : 'reveal-hidden'}`}>
                What We Offer
              </h3>

              <ul className="space-y-3 mb-10" ref={pointsRef}>
                {challenge.points.map((point, i) => (
                  <li
                    key={i}
                    className={`flex items-start gap-3 ${pointsVisible ? 'anim-fade-up' : 'reveal-hidden'}`}
                    style={{ animationDelay: `${i * 0.1}s` }}
                  >
                    <span
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: '#43a047' }}
                    >
                      <CheckCircle2 size={13} className="text-white" />
                    </span>
                    <span className="text-muted-foreground text-sm pt-0.5">{point}</span>
                  </li>
                ))}
              </ul>

              <div
                className={`rounded-2xl p-7 border border-border bg-muted ${pointsVisible ? 'anim-fade-up delay-300' : 'reveal-hidden'}`}
              >
                <h3 className="text-lg font-bold text-foreground mb-2">Ready to Overcome This Challenge?</h3>
                <p className="text-sm text-muted-foreground mb-5">
                  Our team of B2B marketing experts is ready to help you achieve your goals. Contact us today for a free consultation.
                </p>
                <Link
                  to="/contact"
                  className="btn-pulse inline-flex items-center gap-2 text-white text-sm font-semibold px-7 py-3 rounded-full shadow-md"
                  style={{ backgroundColor: '#43a047', transition: 'opacity 0.2s, transform 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  Get Free Consultation <ArrowRight size={14} />
                </Link>
              </div>
            </div>

            {/* ── Sidebar ── */}
            <div className="lg:w-72 space-y-5">
              <div className="bg-muted border border-border rounded-xl p-5">
                <h4 className="font-bold text-foreground mb-4 text-xs uppercase tracking-wider">Your Challenges</h4>
                <ul className="space-y-1">
                  {Object.entries(challenges).map(([key, c]) => (
                    <li key={key}>
                      <Link
                        to={`/${key}`}
                        className={`flex items-center gap-2 text-sm py-2 px-3 rounded-lg ${
                          key === challengeId ? 'text-white font-semibold' : 'text-muted-foreground hover:text-[#43a047] hover:bg-muted'
                        }`}
                        style={{
                          backgroundColor: key === challengeId ? '#43a047' : 'transparent',
                          transition: 'all 0.2s',
                        }}
                      >
                        <ArrowRight size={12} />
                        {c.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-xl p-5 text-white relative overflow-hidden" style={{ backgroundColor: '#43a047' }}>
                <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10" />
                <h4 className="font-bold mb-2 text-sm relative">Talk to an Expert</h4>
                <p className="text-xs text-green-100 leading-relaxed mb-4 relative">
                  Let us help solve your B2B marketing challenges with tailored solutions.
                </p>
                <a
                  href="tel:941-799-8809"
                  className="relative flex items-center gap-2 text-sm font-semibold bg-white text-[#43a047] px-4 py-2 rounded-full"
                  style={{ transition: 'background 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e8f5e9'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}
                >
                  <Phone size={14} />
                  941-799-8809
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengePage;
